import React from "react";
import "./preview-report.css";

/* SendOverlays — the branded full-screen "working" moments (2026-07-13 swap):
 *
 *   QuoteBuildOverlay  — Preview → Create Quotation: drafting tools (ruler,
 *                        scissors, pencil, compass, saw) hammering away over a
 *                        measuring strip + progress bar, then the app lands on
 *                        the client's Quotes tab (flow unchanged).
 *   QuoteSendOverlay   — Store → Send to Sales Team: the factory → truck →
 *                        client delivery scene that used to play on Create
 *                        Quotation (user request: reuse it EXACTLY here).
 *
 * Both are pure CSS keyframes (preview-report.css .pvxq-* / .pvxb-*). */

export function QuoteSendOverlay({ caption = "Sending quotation to client…" }) {
  return (
    <div className="pvxq-overlay" data-testid="pvx-quote-anim" role="status" aria-live="polite">
      <div className="pvxq-brand">LHC<span>STUDIO</span></div>

      <div className="pvxq-scene">
        {/* factory (manufacturer) */}
        <svg className="pvxq-factory" viewBox="0 0 64 48" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 44V20l14 8v-8l14 8v-8l14 8v16z" />
          <path d="M10 12V6h6v10" />
          <path d="M14 44v-8h8v8M30 44v-8h8v8" />
        </svg>

        <div className="pvxq-road"><div className="pvxq-dashes" /></div>

        {/* the travelling truck */}
        <svg className="pvxq-truck" viewBox="0 0 72 40" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="2" y="8" width="42" height="20" rx="2" />
          <path d="M44 14h12l8 8v6h-20z" />
          <circle cx="14" cy="32" r="5" />
          <circle cx="54" cy="32" r="5" />
          <path d="M19 32h30M2 32h7" />
        </svg>

        {/* client / order document */}
        <svg className="pvxq-client" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M28 4H12a4 4 0 0 0-4 4v32a4 4 0 0 0 4 4h24a4 4 0 0 0 4-4V16z" />
          <path d="M28 4v12h12" />
          <path d="M16 26h16M16 33h10" />
        </svg>
      </div>

      <div className="pvxq-caption">{caption}</div>
      <div className="pvxq-bar"><div className="pvxq-bar-fill" /></div>
    </div>
  );
}

/* The workshop tool set — stroke icons, one per beat of the build loop. */
const TOOLS = [
  { key: "ruler", title: "Measuring", d: <><rect x="2" y="9" width="20" height="7" rx="1" transform="rotate(-8 12 12)" /><path d="M6.5 9.6l.8 2.6M10.4 9l.8 2.6M14.3 8.5l.8 2.6M18.2 8l.8 2.6" transform="rotate(-8 12 12)" /></> },
  { key: "scissors", title: "Cutting", d: <><circle cx="6" cy="6" r="2.6" /><circle cx="6" cy="18" r="2.6" /><path d="M8.2 7.6 20 18M8.2 16.4 20 6" /></> },
  { key: "pencil", title: "Drafting", d: <><path d="M17 3l4 4L8 20l-5 1 1-5z" /><path d="M14.5 5.5l4 4" /></> },
  { key: "compass", title: "Setting out", d: <><circle cx="12" cy="5" r="1.8" /><path d="M12 6.8 5.5 21M12 6.8 18.5 21" /><path d="M8 14.5a7.5 7.5 0 0 0 8 0" /></> },
  { key: "saw", title: "Trimming", d: <><path d="M3 15 15 3l4 4-9.5 9.5" /><path d="M5.5 12.5l1.6 1.6M8 10l1.6 1.6M10.5 7.5l1.6 1.6" /><path d="M9.5 16.5 6 20l-3-1 1.5-3.5" /></> },
  { key: "wrench", title: "Fitting", d: <path d="M14.7 6.3a4.5 4.5 0 0 0-6 5.6L3 17.6 6.4 21l5.7-5.7a4.5 4.5 0 0 0 5.6-6l-3 3-2.8-.7-.7-2.8z" /> },
];

export function QuoteBuildOverlay({ caption = "Preparing your quotation…" }) {
  return (
    <div className="pvxq-overlay" data-testid="pvx-quote-anim" role="status" aria-live="polite">
      <div className="pvxq-brand">LHC<span>STUDIO</span></div>

      {/* the workbench: tools take turns "working" over a measuring strip */}
      <div className="pvxb-bench">
        <div className="pvxb-tools">
          {TOOLS.map((t, i) => (
            <svg key={t.key} className="pvxb-tool" style={{ animationDelay: `${i * 0.34}s` }}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <title>{t.title}</title>
              {t.d}
            </svg>
          ))}
        </div>
        <div className="pvxb-strip"><div className="pvxb-ticks" /></div>
      </div>

      <div className="pvxq-caption pvxb-caption">
        {caption}
        <span className="pvxb-steps">measuring · cutting · pricing</span>
      </div>
      <div className="pvxq-bar"><div className="pvxq-bar-fill" /></div>
    </div>
  );
}
